'use strict'

import get from 'lodash.get'
import pclnRequest from '@pcln/request'
import {
  buildExperimentData,
  checkIsValidImpression,
  generateUUID,
  getAceEndpoint,
  getClientGlobals,
  getClientGuid,
  getCookieByName,
  getMetadataHash,
  getServerGlobals,
  isBrowser,
} from '../utils/generic-utilities'
import ConfigConstants from '../utils/constants'
import { idleEnqueue } from '../utils/idle-until-urgent'
import PCLNAnalytics from '@pcln/analytics'

const ReqFields = [
  { name: 'appCode', required: true, regex: /^[a-zA-Z_]{1,20}$/ },
  { name: 'productID', required: true, regex: /^\d{1,2}$/ },
  { name: 'productSubType', required: true, regex: /^(ALL|RTL|OPQ|SOPQ)$/ },
  { name: 'pageName', required: false, regex: /^.{1,50}[a-zA-Z_].{1,80}$/ },
  { name: 'platform', required: false, regex: /^.{1,50}[a-zA-Z_].{1,80}$/ },
]

var productId = -1

class PCLNExpt {
  constructor() {
    this._reset()
  }

  _logImpressionToGA(experiment_name, productID, pageName, platform, setiId, variantId) {
    if (typeof PCLNAnalytics.logEvent === 'function') {
      const attributes = {
        page_name: pageName,
        plat_form: platform,
        product_id: productID,
        experiment_name: experiment_name,
        seti_id: setiId,
        variant_id: variantId,
      }
      PCLNAnalytics.GA4Enabled = true
      PCLNAnalytics.logEvent({
        event: 'seti',
        attributes,
      })
    }
  }

  // We need some initial data before we can do anything useful with this instance.
  initialize(experimentAssignments, webAppMetadata, config, req, overrides) {
    const isServerSide = !isBrowser()
    this.__environment = isServerSide ? global : window
    this.__globals = isServerSide ? getServerGlobals(req) : getClientGlobals(window, document)

    this.__metadata = this._getFullMetadata(webAppMetadata, overrides)
    // Initializing clientGuid from cookie
    this.__clientGuid = overrides?.cguid ?? getClientGuid(this.__globals)
    this.__experimentData = buildExperimentData(experimentAssignments)
    this.__setTimeout = this.__environment.setTimeout.bind(this.__environment)
    this.__clearTimeout = this.__environment.clearTimeout.bind(this.__environment)

    const { setiBaseInternalURL = '', setiBaseExternalURL = '' } = config || {}
    this.__configuredEndpoints = {
      internalEndpoint: setiBaseInternalURL + ConfigConstants.internalEndpoint,
      externalEndpoint: setiBaseExternalURL + ConfigConstants.externalEndpoint,
    }

    if (config) {
      this.__setiUrl = config.setiUrl

      this.__useServiceDiscovery = config.discovery

      if (config.useBatching === !!config.useBatching) {
        this.__useBatching = !!config.useBatching
      }
      // There is a minimum of 500 for this config param.
      // If the caller passes in anything less than 500, we use the default of 1000.
      if (!isNaN(config.batchIntervalMS) && config.batchIntervalMS >= 500) {
        this.__impressionBatchIntervalMS = config.batchIntervalMS
      }

      // Obviously the batch size must be at least 1.  A flute with no holes is not a flute.
      if (!isNaN(config.batchSize) && config.batchSize > 0) {
        this.__impressionBatchSize = config.batchSize
        this.__eventBatchSize = config.batchSize
      }

      if (!isNaN(config.timeOutForApiCalls)) {
        this.__timeOutForApiCalls = config.timeOutForApiCalls
      }

      if (config.debugFireImpression) {
        this.__debugFireImpression = true
      }

      this.__logger = get(config, 'logger')

      const { isIdleQueued = false } = config
      this.__isIdleQueue = isIdleQueued
    }
  }

  reset() {
    this._reset()
  }

  // Returns respective variant (DEFAULT, VARIANT, CONTROL) of the test.
  // GMS (08/31/2017) removed isDefault, isVariant functions as host app developers
  // can use getTestState() and act accordingly.
  getTestState(experimentTagName) {
    return this._getAssignedVariantName(experimentTagName)
  }

  // Some convenience functions to match what products already use in older versions.
  //    These just wrap the _check and _get functions above.

  // Returns true for variant === 'DEFAULT', else false
  // @Deprecated
  isDefault(experimentTagName) {
    return this._checkAssignedVariantName(experimentTagName, 'DEFAULT')
  }

  // Returns true for any variant !== 'DEFAULT', including 'CONTROL'
  // @Deprecated
  isVariant(experimentTagName) {
    return !this._checkAssignedVariantName(experimentTagName, 'DEFAULT')
  }

  // Returns true for any variant !== 'DEFAULT' and variant !==  'CONTROL'
  // @Deprecated
  isVariantButNotControl(experimentTagName) {
    return (
      !this._checkAssignedVariantName(experimentTagName, 'DEFAULT') &&
      !this._checkAssignedVariantName(experimentTagName, 'CONTROL')
    )
  }

  _processFireAction(items, metadataOverrides, isImmediateImpression, useBatching, isImpression) {
    const fireActions = {
      fireImmediate: isImpression
        ? this._fireSetiImpressionImmediate.bind(this)
        : this._fireSetiEventsImmediate.bind(this),
      addToBatch: isImpression
        ? this._addSetiImpressionToBatchQueue.bind(this)
        : this._addSetiEventToBatchQueue.bind(this),
    }

    if (!isImmediateImpression && useBatching) {
      return fireActions.addToBatch(items, metadataOverrides)
    } else {
      return fireActions.fireImmediate(items, metadataOverrides)
    }
  }

  _addPushParamsToDataLayer(options, experimentTagName, experimentData) {
    // We need to send a GA Event if either:
    //    (1) the web app passed in a ga object in its options object
    //    (2) there is a gaDimension property in the experimentAssignments
    //        data for this experiment.

    // Push to a GA custom dimension if GA data was provided in options object.
    // This block is parallel to Evan's recent change to the abtesting module
    const gaDimension =
      get(options, 'ga.dimension') || get(experimentData, `${experimentTagName}.gaDimension`)
    const teamName = get(options, 'ga.teamName') || get(experimentData, `${experimentTagName}.setiTeamName`)

    if (gaDimension && teamName) {
      // Set up parameters
      const expVariantToken = this._getAssignedVariantName(experimentTagName)
      const pushParams = Object.assign(
        {},
        {
          event: 'seti',
          seti_team_name: teamName,
          gaDimension: experimentTagName + '|' + expVariantToken,
        }
      )

      // Make sure window object is available otherwise will break if tried pushing GA dimesion server side
      // Make sure GTM dataLayer is available, then fire dataLayer.push on SETI impression
      typeof window !== 'undefined' &&
        typeof get(window, 'dataLayer.push') === 'function' &&
        window.dataLayer.push(pushParams)
    }
  }

  fireImpression(experimentTagName, metadataOverrides = {}, options = {}) {
    if (arguments.length >= 4) {
      throw new Error('Callbacks no longer supported. Use promises')
    }
    let pageName = ''
    let platform = ''
    let setiId = ''
    let variantId = ''
    let promise = Promise.resolve({})
    // TODO: next refactoring at v5.0.1 will include test
    //       code and coverage will be coded to un-ignore, and back up to 100%.
    /* istanbul ignore else */
    if (!this.__isIdleQueue || options.immediateImpression) {
      // dont fire impression if
      // 1. it has already been fired for this experiment
      // 2. there is no experimentTagName
      // 3. it's not a valid impression
      if (
        this.__firedImpressions[experimentTagName] ||
        !experimentTagName ||
        !checkIsValidImpression(this.__experimentData, experimentTagName, this.__debugFireImpression)
      ) {
        return Promise.resolve(false)
      }

      // set true flag so the same experiment impression does not fire again
      this.__firedImpressions[experimentTagName] = true
      promise = this._processFireAction(
        experimentTagName,
        metadataOverrides,
        options.immediateImpression,
        this.__useBatching,
        true
      )

      if (metadataOverrides && Object.keys(metadataOverrides).length > 0) {
        if (metadataOverrides.pageName) {
          pageName = metadataOverrides.pageName
        }
        if (metadataOverrides.platform) {
          platform = metadataOverrides.platform
        }
      }

      setiId = this._getAssignedSetiId(experimentTagName)
      variantId = this._getAssignedSetiVariantId(experimentTagName)

      if (metadataOverrides && metadataOverrides.pageName) {
        this._logImpressionToGA(experimentTagName, productId, pageName, platform, setiId, variantId)
      }
      this._addPushParamsToDataLayer(options, experimentTagName, this.__experimentData)
    } else {
      // TODO: once v5.0.0-beta-* passes integration test with relax the test
      //       refactor to omit the "bridgedThis" pattern, perhaps w. .bind
      //       or something more rubust for clean code.
      const bridgedThis = this
      idleEnqueue(() => {
        // dont fire impression if
        // 1. it has already been fired for this experiment
        // 2. there is no experimentTagName
        // 3. it's not a valid impression
        if (
          bridgedThis.__firedImpressions[experimentTagName] ||
          !experimentTagName ||
          !checkIsValidImpression(
            bridgedThis.__experimentData,
            experimentTagName,
            bridgedThis.__debugFireImpression
          )
        ) {
          return Promise.resolve(false)
        }

        // set true flag so the same experiment impression does not fire again
        bridgedThis.__firedImpressions[experimentTagName] = true
        promise = bridgedThis._processFireAction(
          experimentTagName,
          metadataOverrides,
          false,
          bridgedThis.__useBatching,
          true
        )

        bridgedThis._addPushParamsToDataLayer(options, experimentTagName, bridgedThis.__experimentData)
      })
    }
    return promise
  }

  // This is a utility method that populates as much as possible
  //    of a web app's local experimentAssignments object from
  //    the res.local object generated by the server-side node.js code.
  // TODO: comment this function, is not need to implement, should remove it
  // resLocalsToComponentSeti () {
  // placeholder - do we need this?
  // }

  // Added to send other event types (PAGE_VIEW, UI_INTERACTION, ERROR, OFFER) to seti v4
  fireEvent(eventDetails, options, metadataOverrides) {
    if (arguments.length >= 4) {
      throw new Error('Callbacks no longer supported. Use promises')
    }

    const firedEventsType = get(eventDetails, 'eventType', 'otherEvent')
    if (this.__firedEvents.has(firedEventsType)) {
      return Promise.resolve(false)
    }

    this.__firedEvents.set(firedEventsType, true)
    return this._processFireAction(
      eventDetails,
      metadataOverrides,
      get(options, 'immediateImpression'),
      this.__useBatching,
      false
    )
  }

  clearBatch() {
    return Promise.all([this._resolveBatchQueuedImpressions(), this._resolveBatchQueuedEvents()])
  }

  _reset() {
    // This property will hold the metadata that needs to be sent with each impression request,
    //    e.g. pageName, etc.
    this.__metadata = {}

    this.__clientGuid = null

    this.__productSubType = null

    this.__productID = null

    // This property will hold the current list of assignments, as received from the web app
    //   and as updated/added to by new roll calls if relevant.
    this.__experimentData = {}

    // Record fired impressions in this Map so that we don't repeat impressions for the same experiment name.
    this.__firedImpressions = new Map()

    // Record fired events so that we don't repeat event recording for the same experiment name
    this.__firedEvents = new Map()

    // This array holds a queue of the impressions waiting to be sent to the service in a batch.
    //    It is periodically flushed and the contents sent to the service.``
    //    The schedule for sending the contents is defined by some parameters below.
    this.__batchQueue = []

    // To store non-impression event types of seti
    this.__eventBatchQueue = []

    // We default this to true and only send impressions singly if web app passes useBatching:false in config.
    this.__useBatching = true

    this.__impressionBatchIntervalMS = 1000 // Default value - web app can override in config.
    this.__impressionBatchSize = 10 // Default value - web app can override in config.
    this.__impressionBatchTimeoutReference = null // Holds the reference returned by setTimeout for later clearing if necessary.
    this.__impressionBatchTimeoutStarted = false // Flag that tells the component that we are currently working on a batch

    this.__eventBatchSize = 10
    this.__eventBatchIntervalMS = 1000
    this.__eventBatchTimeoutReference = null
    this.__eventBatchTimeoutStarted = false

    // this property is used to define timeout for seti api calls. This is controllable by
    // each host app developers as necessary. Default will be 5000
    this.__timeOutForApiCalls = 5000

    // If set to true in the initialize config, impressions will always fire, regardless of result code.
    // This is intended to be used for debugging purposes only.
    this.__debugFireImpression = false
  }

  _validateInitializationMetadata(metadata = null) {
    // TODO - discuss whether this method should throw errors or just return false.

    // This is an example of what we expect to be given by the web app to initialize.
    //    The details may change as we develop if we find that we would rather obtain something ourselves
    //    from a cookie or the browser etc. instead of from the initalization call or equivalent.
    // Check for the existence of metadata object itself
    if (!metadata) {
      throw new Error('Metadata is null')
    }

    ReqFields.forEach((item) => {
      if (item.required && !metadata[item.name]) {
        throw new Error(`required property ${item.name} is missing`)
      }

      if (!item.regex.test(metadata[item.name])) {
        throw new Error(`${item.name} is badly-formed`)
      }
    })

    return true
  }

  // This method combines the metadata passed in by the web app with the metadata that we can derive
  //    from the browser in this code.
  _getFullMetadata(metadataFromWebApp, overrides) {
    // This is an example of the metadata that we expect to have available when making SETI service calls.
    //    The details may change as we develop if we find that we would rather obtain something ourselves
    //    from a cookie or the browser etc. instead of passed in by the web app via the initalization call or equivalent.
    // For the items that we can obtain/derive ourselves, we should still allow the web app to set it
    //    if they need to for some reason.

    // Validate web app metadata.  If web app passed in no metadata, then we are going to miss some required data.  Throw error.
    try {
      this._validateInitializationMetadata(metadataFromWebApp)
    } catch (ex) {
      throw new Error('Metadata validation error: ' + ex)
    }

    this.__productID = get(metadataFromWebApp, 'productID', -1)
    productId = this.__productID
    this.__productSubType = get(metadataFromWebApp, 'productSubType', 'ALL')

    const requestGuid = generateUUID()

    const referralSourceID = overrides?.referral?.sourceId ?? metadataFromWebApp.referralSourceID
    const referralID = overrides?.referral?.id ?? metadataFromWebApp.referralID
    const referralClickID = overrides?.referral?.clickId ?? metadataFromWebApp.referralClickID

    const visitID = getCookieByName('vid', this.__globals) || null

    const appCode = metadataFromWebApp.appCode

    const pageName = metadataFromWebApp.pageName

    const platform = metadataFromWebApp.platform

    const userAgent = overrides?.userAgent ?? get(this.__globals, 'ua') ?? 'UNKNOWN'

    const httpReferrer = get(this.__globals, 'ref')

    return {
      rguid: requestGuid,
      referralSourceID,
      referralID,
      referralClickID,
      visitID,
      appCode,
      pageName,
      userAgent,
      httpReferrer,
      platform,
    }
  }

  // Returns true or false depending on whether variant variantNameToCheck is assigned for experimentTagName.
  _checkAssignedVariantName(experimentTagName, variantNameToCheck) {
    return this._getAssignedVariantName(experimentTagName) === variantNameToCheck
  }

  // Returns the assigned variant name for the given experimentTagName.
  _getAssignedVariantName(experimentTagName) {
    return get(this.__experimentData, [experimentTagName, 'setiVariantName'], 'DEFAULT')
  }

  _getAssignedSetiId(experimentTagName) {
    return get(this.__experimentData, [experimentTagName, 'setiId'])
  }

  _getAssignedSetiVariantId(experimentTagName) {
    return get(this.__experimentData, [experimentTagName, 'setiVariantId'])
  }

  // Faking a hash here by just concatenating the key/value pairs in the object.
  //   There might be some hole in this that I haven't detected.

  _addSetiImpressionToBatchQueue(experimentTagName, metadataOverrides) {
    let promise = Promise.resolve(false)

    this.__batchQueue.push({
      experimentTagName,
      metadataOverrides,
    })

    // We will send all the currently-batched SETI impression calls whenever one of these things happens:
    //     - the current impression being pushed results in the this.__batchQueue reaching or exceeding the this.__impressionBatchSize limit
    //     - we've reached this.__impressionBatchIntervalMS milliseconds after the time at which the earliestr impression was pushed into the current queue.

    if (this.__batchQueue.length >= this.__impressionBatchSize) {
      promise = this._resolveBatchQueuedImpressions()
    }

    // If we don't currently have a batch started, then set a timeout to resolve the batch after _batchIntervalMS milliseconds
    //     and set flag to tell the component that we are currently working on a batch.
    if (!this.__impressionBatchTimeoutStarted) {
      this.__impressionBatchTimeoutReference = this.__setTimeout(
        this._resolveBatchQueuedImpressions.bind(this),
        this.__impressionBatchIntervalMS
      )
      this.__impressionBatchTimeoutStarted = true
    }

    return promise
  }

  _resolveBatchQueuedImpressions() {
    // We are taking everything in the batch queue and sending it now.
    //     Clear the current timeout, if any, in case this is being called before the scheduled timeout due to reaching the batchSize limit.
    //     Set flag to tell the component that we are not currently working on a batch.
    this.__clearTimeout(this.__impressionBatchTimeoutReference)
    this.__impressionBatchTimeoutStarted = false

    const allItemsToSend = this.__batchQueue.splice(0, this.__batchQueue.length)

    return this._fireSetiImpressionsBatched(allItemsToSend)
  }

  _fireSetiImpressionsBatched(batchToSend) {
    if (!batchToSend || !batchToSend.length) {
      // No batch array found.  Get out.
      return Promise.resolve(false)
    }

    let pageName = ''
    let platform = ''
    let setiId = ''
    let variantId = ''

    // build the url
    const batchImpressionUrl = ConfigConstants.baseImpressionUrl + this.__clientGuid

    // MG 3/6/2017:  we need to make a call for each distinct metadata object
    //    stored in batchToSend.
    // So create a temporary object that holds arrays of batches to send
    //    keyed off of a representation of the metadata.
    const batchesByMetadataHash = {}
    for (let idx = 0; idx < batchToSend.length; idx++) {
      const metadataHash = getMetadataHash(batchToSend[idx].metadataOverrides)
      if (!batchesByMetadataHash[metadataHash]) {
        batchesByMetadataHash[metadataHash] = []
      }
      batchesByMetadataHash[metadataHash].push(batchToSend[idx])
    }

    return Promise.all(
      Object.keys(batchesByMetadataHash).map((vY) => {
        const currentBatchToSend = batchesByMetadataHash[vY]
        // metadataOverrides must be the same for every item in the currentBatchToSend array.
        //    So just grab it from item 0.
        const currentMetadataOverrides = currentBatchToSend[0].metadataOverrides

        if (currentMetadataOverrides && Object.keys(currentMetadataOverrides).length > 0) {
          if (currentMetadataOverrides.pageName) {
            pageName = currentMetadataOverrides.pageName
          }
          if (currentMetadataOverrides.platform) {
            platform = currentMetadataOverrides.platform
          }
        }

        const listOfSetiEvents = []
        for (let idx = 0; idx < currentBatchToSend.length; idx++) {
          setiId = this._getAssignedSetiId(currentBatchToSend[idx].experimentTagName)
          variantId = this._getAssignedSetiVariantId(currentBatchToSend[idx].experimentTagName)

          if (currentMetadataOverrides && currentMetadataOverrides.pageName) {
            // we can check if(pageName)
            this._logImpressionToGA(
              currentBatchToSend[idx].experimentTagName,
              productId,
              pageName,
              platform,
              setiId,
              variantId
            )
          }

          listOfSetiEvents.push({
            experimentTagName: currentBatchToSend[idx].experimentTagName,
          })
        }

        const request = this._formRequestArray(currentMetadataOverrides, listOfSetiEvents, 'setiEvents')

        return this._makeServiceCall(batchImpressionUrl, request)
      })
    )
  }

  _fireSetiImpressionImmediate(experimentTagName, metadataOverrides) {
    const batchImpressionUrl = ConfigConstants.baseImpressionUrl + this.__clientGuid

    const listOfSetiEvents = []

    listOfSetiEvents.push({ experimentTagName })

    const request = this._formRequestArray(metadataOverrides, listOfSetiEvents, 'setiEvents')

    return this._makeServiceCall(batchImpressionUrl, request)
  }

  _addSetiEventToBatchQueue(eventDetails, metadataOverrides) {
    let promise = Promise.resolve(false)

    this.__eventBatchQueue.push({
      metadataOverrides,
      eventDetails,
    })

    if (this.__eventBatchQueue.length >= this.__eventBatchSize) {
      promise = this._resolveBatchQueuedEvents()
    }

    if (!this.__eventBatchTimeoutStarted) {
      this.__eventBatchTimeoutReference = this.__setTimeout(
        this._resolveBatchQueuedEvents.bind(this),
        this.__eventBatchIntervalMS
      )
      this.__eventBatchTimeoutStarted = true
    }

    return promise
  }

  _resolveBatchQueuedEvents() {
    this.__clearTimeout(this.__eventBatchTimeoutReference)
    this.__eventBatchTimeoutStarted = false

    const allEventsToSend = this.__eventBatchQueue.splice(0, this.__eventBatchQueue.length)

    return this._fireSetiEventsBatched(allEventsToSend)
  }

  _fireSetiEventsBatched(eventsToSend) {
    if (!eventsToSend || !eventsToSend.length) {
      return Promise.resolve(false)
    }
    const batchEventUrl = ConfigConstants.baseEventUrl + this.__clientGuid

    const batchesByMetadataHash = {}
    for (let idx = 0; idx < eventsToSend.length; idx++) {
      const metadataHash = getMetadataHash(eventsToSend[idx].metadataOverrides)
      if (!batchesByMetadataHash[metadataHash]) {
        batchesByMetadataHash[metadataHash] = []
      }
      batchesByMetadataHash[metadataHash].push(eventsToSend[idx])
    }

    return Promise.all(
      Object.keys(batchesByMetadataHash).map((vY) => {
        const currentBatchToSend = batchesByMetadataHash[vY]
        const currentMetadataOverrides = currentBatchToSend[0].metadataOverrides

        const request = this._formNonImpressionEventRequest(currentBatchToSend, currentMetadataOverrides)

        return this._makeServiceCall(batchEventUrl, request)
      })
    )
  }

  _formNonImpressionEventRequest(currentBatchToSend, metadataOverrides) {
    var pageViewEvents = []
    var uiInteractionEvents = []
    var errorEvents = []
    var eventTypes = []
    for (let idx = 0; idx < currentBatchToSend.length; idx++) {
      switch (currentBatchToSend[idx].eventDetails.eventType) {
        case 'pageViewEvent':
          pageViewEvents.push(currentBatchToSend[idx].eventDetails)
          break
        case 'uiInteractionEvent':
          uiInteractionEvents.push(currentBatchToSend[idx].eventDetails)
          break
        case 'errorEvent':
          errorEvents.push(currentBatchToSend[idx].eventDetails)
          break
        default:
          break
      }
    }
    if (pageViewEvents.length > 0) {
      eventTypes.push('PAGE_VIEW')
    }
    if (uiInteractionEvents.length > 0) {
      eventTypes.push('UI_INTERACTION')
    }
    if (errorEvents.length > 0) {
      eventTypes.push('ERROR')
    }

    return {
      eventTypes,
      pageViewEvents,
      uiInteractionEvents,
      errorEvents,
      headerInfo: this.__metadata,
      productID: get(metadataOverrides, 'productID', this.__productID),
      productSubType: get(metadataOverrides, 'productSubType', this.__productSubType),
    }
  }

  _fireSetiEventsImmediate(eventDetails, metadataOverrides) {
    const batchEventUrl = ConfigConstants.baseEventUrl + this.__clientGuid
    // create single batch to reuse _formNonImpressionEventRequest function
    const singleEventToSend = [{ eventDetails }]
    const request = this._formNonImpressionEventRequest(singleEventToSend, metadataOverrides)

    return this._makeServiceCall(batchEventUrl, request)
  }

  _formRequestArray(metadataOverrides, events, eventName) {
    return {
      headerInfo: this.__metadata,
      [eventName]: events,
      productID: get(metadataOverrides, 'productID', this.__productID),
      productSubType: get(metadataOverrides, 'productSubType', this.__productSubType),
    }
  }

  _isFullURI(uri) {
    try {
      new URL(uri)
      return true
    } catch (e) {
      return false
    }
  }

  _makeServiceCall(uri, data) {
    if (this.__setiUrl) {
      return this._ServiceCall('', uri, data, {
        url: this.__setiUrl + uri,
      })
    } else if (isBrowser() || this.__useServiceDiscovery) {
      // When using service discovery, uri must include the full endpoint
      const fullServiceUri = getAceEndpoint(this.__configuredEndpoints) + uri

      const options = this._isFullURI(fullServiceUri)
        ? { discovery: true, url: fullServiceUri }
        : { discovery: true }

      return this._ServiceCall(ConfigConstants.consulServiceName, fullServiceUri, data, options)
    } else {
      return this._ServiceCall(ConfigConstants.nodeConfigServiceName, uri, data)
    }
  }

  _ServiceCall(serviceName, uri, data, options = {}) {
    const requestOptions = Object.assign(
      options,
      { serviceName, data, endpoint: uri, method: 'post' },
      { logger: this.__logger, timeout: this.__timeOutForApiCalls },
      {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          Accept: 'application/json',
        },
      }
    )

    return pclnRequest(requestOptions)
      .then((data) => {
        return data
      })
      .catch((_err) => {
        throw new Error(_err)
      })
  }
}

export default PCLNExpt
